import {getCurrentProjectName} from '../url';
import {Helmet} from '@dr.pogodin/react-helmet';

const projectName = getCurrentProjectName();
const TermsConditionsPage = () => {
    return (
        <>
            <Helmet>
                <title>Terms & Conditions - {projectName}</title>
                <link rel="canonical" href={`https://lesia-k.com/terms-condition`}/>

                <meta name="description"
                      content={`Read the Terms & Conditions for using ${projectName}. Understand your rights and responsibilities when accessing our website and services.`}/>
                <meta name="keywords"
                      content={`terms and conditions, ${projectName}, website terms, user agreement, legal information`}/>
                <meta property="og:title" content={`Terms & Conditions - ${projectName}`}/>
                <meta property="og:description"
                      content={`Read the Terms & Conditions for using ${projectName}. Understand your rights and responsibilities when accessing our website and services.`}/>
                <meta property="og:url" content="https://lesia-k.com/terms-condition"/>
                <meta name="twitter:title" content={`Terms & Conditions - ${projectName}`}/>
                <meta name="twitter:description"
                      content={`Read the Terms & Conditions for using ${projectName}. Understand your rights and responsibilities when accessing our website and services.`}/>
            </Helmet>
            <section className="w-screen text-white p-20">
                <h1 className="text-2xl text-center">Terms & Conditions</h1>
                <div className="text-white">
                    <h2 className="text-2xl font-semibold mb-4">1. General Terms and Conditions</h2>

                    <p className="mb-4">Welcome to {projectName}! By accessing and using this website, you agree to
                        comply
                        with and be bound by the following terms and conditions of use. If you disagree with any part of
                        these terms and conditions, please do not use our website.</p>

                    <p className="mb-2"><strong>1.1. Acceptance of Terms:</strong> These Terms and Conditions constitute
                        a
                        legally binding agreement between you and {projectName} (“we,” “us,” or “our”). Your use of this
                        website signifies your acceptance of these terms.</p>

                    <p className="mb-2"><strong>1.2. Changes to Terms:</strong> We reserve the right to modify or amend
                        these Terms and Conditions at any time. Any changes will be effective immediately upon posting
                        on
                        this website. Your continued use of the website following the posting of changes will mean that
                        you
                        accept those changes.</p>

                    <p className="mb-2"><strong>1.3. Use of Website:</strong> You agree to use this website only for
                        lawful
                        purposes. You must not use this website in any way that causes, or is likely to cause, damage to
                        the
                        website or impairment of the availability or accessibility of the website.</p>

                    <p className="mb-2"><strong>1.4. Intellectual Property:</strong> The content, layout, design, data,
                        databases, and graphics on this website are protected by intellectual property laws. You must
                        not
                        reproduce, duplicate, copy, sell, resell, or exploit any portion of this website without our
                        express
                        written permission.</p>

                    <p className="mb-4"><strong>1.5. Limitation of Liability:</strong> To the maximum extent permitted
                        by
                        law, we shall not be liable for any direct, indirect, incidental, consequential, or punitive
                        damages
                        arising out of your access to or use of this website.</p>

                    <h2 className="text-2xl font-semibold mb-4">2. Referrals to Artists</h2>

                    <p className="mb-4"><strong>2.1. Artist Attribution:</strong> We may, from time to time, feature
                        artwork
                        created by various artists. We are committed to providing proper attribution and recognition to
                        these artists. If you are an artist featured on our website and believe that your attribution is
                        incorrect or incomplete, please contact us immediately.</p>

                    <p className="mb-4"><strong>2.2. Artist Rights:</strong> We respect the intellectual property rights
                        of
                        artists. Any artwork displayed on this website is used with the understanding that it is either:
                    </p>

                    <ul className="list-disc list-inside mb-4">
                        <li>Used with the artist's explicit permission.</li>
                        <li>Used under fair use principles for the purpose of showcasing our team's capabilities.</li>
                        <li>Sourced from public domain or publicly available sources.</li>
                    </ul>

                    <p className="mb-4"><strong>2.3. Artist Contact:</strong> For any inquiries regarding artist
                        attributions or rights, please contact us at <a
                            href="mailto:support@mbox-meet.com">support@mbox-meet.com</a>.
                        Artists who own some art from this website
                        <ul className="list-disc list-inside mb-4 text-sm text-gray-500">
                            <li><a href="https://www.linkedin.com/in/artpod2d">Artem</a></li>
                            <li><a href="https://www.linkedin.com/in/baloo-bear">Baloo</a>
                            </li>
                            <li><a
                                href="https://www.linkedin.com/in/victoria-scherbak">Victoria</a>
                            </li>
                        </ul>
                    </p>

                    <h2 className="text-2xl font-semibold mb-4">3. Art as Examples of Team Capabilities</h2>

                    <p className="mb-4"><strong>3.1. Showcase Purpose:</strong> All artwork displayed on this website is
                        intended solely as an example of the artistic and creative capabilities of our team. It serves
                        to
                        demonstrate the range of styles, techniques, and concepts that we can produce.</p>

                    <p className="mb-4"><strong>3.2. Non-Commercial Use:</strong> The artwork shown on this website is
                        not
                        intended for commercial use or reproduction by visitors. You must not use any of the displayed
                        artwork for any purpose without obtaining explicit written permission from us and, where
                        applicable,
                        the original artist or relevant rights holder.</p>

                    <p className="mb-4"><strong>3.3. Public Source Art:</strong> Some of the artwork displayed on this
                        website may be sourced from public domain or publicly available resources. We use these images
                        under
                        the understanding that they are free for use. However, we also acknowledge that attribution may
                        still be required in some cases, and we are committed to providing such attribution where
                        necessary.
                    </p>

                    <p className="mb-4"><strong>3.4. Customization and Original Work:</strong> While the artwork shown
                        provides examples of our team's abilities, we are capable of creating entirely original and
                        customized artwork to meet specific client needs. Contact us to discuss your project
                        requirements.
                    </p>

                    <p className="mb-4"><strong>3.5. No Guarantee of Exact Replication:</strong> While we strive to
                        maintain
                        high standards of quality, we do not guarantee that any commissioned work will be an exact
                        replication of the examples shown on this website. Each project is unique, and final results may
                        vary based on client specifications and creative interpretations.</p>

                    <h2 className="text-2xl font-semibold mb-4">4. Governing Law</h2>

                    <p className="mb-4">These Terms and Conditions shall be governed by and construed in accordance with
                        the
                        laws of Ukraine. Any disputes relating to these terms shall be subject to the exclusive
                        jurisdiction
                        of the courts of Ukraine.</p>

                    <h2 className="text-2xl font-semibold mb-4">5. Contact Information</h2>

                    <p className="mb-4">If you have any questions about these Terms and Conditions, please contact us
                        at:
                        support@mbox-meet.com</p>

                </div>
            </section>
        </>
    )
}

export default TermsConditionsPage;
